import { isLovablePreview } from '@/lib/utils/environment';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import {
  useGetDealLineItemsQuery,
  useSetDealLineItemsMutation,
  type LineItem as GQLLineItem,
} from '@/lib/graphql/types-and-hooks';
import { useDealProducts } from '@/hooks/useDealProducts';
import type { DealLineItem } from '@/hooks/useDealProducts';

// Minimal structural shape fromBackend reads — satisfied by both the
// getDealLineItems query result (a narrower inline type) and the inline
// DealRelations line items (full GQLLineItem).
type LineItemLike = {
  productId?: string | null;
  properties?: { name?: string | null; quantity?: number | null; unitPrice?: number | null } | null;
};

function fromBackend(gql: LineItemLike): DealLineItem {
  return {
    productId: gql.productId ?? '',
    name: gql.properties?.name ?? '',
    quantity: gql.properties?.quantity ?? 1,
    price: gql.properties?.unitPrice ?? 0,
  };
}

/**
 * Per-deal line items hook with dual-mode support.
 *
 * Preview mode: sticky state per dealId (shared with DealProductsPicker / DealDetailSheet).
 * Production mode: lazy GraphQL query + setDealLineItems batch mutation.
 *
 * @param inlineLineItems — line items already present on the deal object (from the
 *   DealRelations fragment). Used as an immediate data source while the dedicated
 *   getDealLineItems query is still loading or if it returns empty.
 */
export function useDealLineItems(dealId: string | null | undefined, inlineLineItems?: (GQLLineItem | null)[] | null) {
  const isPreview = isLovablePreview();
  const workspaceId = useWorkspaceId();

  const preview = useDealProducts(dealId);

  const { data, loading, refetch } = useGetDealLineItemsQuery({
    variables: { dealId: dealId ?? '', workspaceId: workspaceId ?? '' },
    skip: !dealId || !workspaceId || isPreview,
  });

  const [setDealLineItemsMutation] = useSetDealLineItemsMutation();

  // Prefer the dedicated query result; fall back to inline deal.lineItems
  // (already available from the parent getDeals/getDeal query).
  const queryItems = (data?.dealLineItems ?? []).filter((li): li is NonNullable<typeof li> => li != null);
  const inlineItems = (inlineLineItems ?? []).filter((li): li is GQLLineItem => li != null);
  const backendItems = queryItems.length > 0 ? queryItems : inlineItems;

  const items: DealLineItem[] = isPreview
    ? preview.items
    : backendItems.map(fromBackend);

  const setItems = async (next: DealLineItem[]) => {
    if (isPreview) {
      preview.setItems(next);
      return;
    }
    if (!dealId || !workspaceId) return;
    await setDealLineItemsMutation({
      variables: {
        workspaceId,
        dealId,
        lineItems: next.map(li => ({
          ...(li.productId && { productId: li.productId }),
          name: li.name,
          quantity: li.quantity,
          unitPrice: li.price,
          listPrice: li.price,
        })),
      },
    });
    await refetch();
  };

  return { items, loading, setItems };
}
